import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Ban, CalendarClock, CalendarSync, Check, X } from "lucide-react";
import client from "@/api/client";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

function BookingRow({ booking, isMentorView, isAdmin, onUpdated }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [moving, setMoving] = useState(false);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [manual, setManual] = useState({ date: "", time: "" });
  const req = booking.cancel_request || {};
  const resched = booking.reschedule_request || {};
  const cancelPending = req.status === "pending";
  const reschedPending = resched.status === "pending";
  const canDecide = isMentorView || isAdmin;

  const slotLabel = (isoStart) => {
    try { return format(parseISO(isoStart), "EEE, MMM d · HH:mm"); } catch { return isoStart; }
  };

  const openMove = async () => {
    setMoving((v) => !v);
    if (slots !== null) return;
    try {
      const res = await client.get(`/booking/availability/${booking.mentor_id}`);
      setSlots((res.data?.slots || []).filter((s2) => s2.start_time !== booking.start_time));
    } catch {
      setSlots([]);
    }
  };

  const proposeTime = async () => {
    let start_time = slot;
    let end_time = (slots || []).find((s2) => s2.start_time === slot)?.end_time || "";
    if (!start_time && manual.date && manual.time) {
      const startDate = new Date(`${manual.date}T${manual.time}:00`);
      start_time = startDate.toISOString();
      end_time = new Date(startDate.getTime() + 30 * 60000).toISOString();
    }
    if (!start_time || !end_time) {
      toast.error(t("memberBookings.pickSlotFirst", { defaultValue: "Pick a new time first" }));
      return;
    }
    setBusy(true);
    try {
      await client.post(`/booking/${booking.booking_id}/reschedule-request`, { start_time, end_time, reason });
      toast.success(t("memberBookings.moveRequested", { defaultValue: "New time proposed — the mentor will confirm." }));
      setMoving(false);
      setSlot("");
      setManual({ date: "", time: "" });
      setReason("");
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const decideMove = async (approve) => {
    setBusy(true);
    try {
      await client.post(`/booking/${booking.booking_id}/reschedule-decision`, { approve });
      toast.success(approve
        ? t("memberBookings.moveApproved", { defaultValue: "Session moved to the new time." })
        : t("memberBookings.moveDeclined", { defaultValue: "New time declined — the original slot stands." }));
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      await client.put(`/booking/${booking.booking_id}/status`, { status });
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const requestCancel = async () => {
    setBusy(true);
    try {
      await client.post(`/booking/${booking.booking_id}/cancel-request`, { reason });
      toast.success(t("memberBookings.cancelRequested", { defaultValue: "Cancellation requested — the mentor will confirm." }));
      setAsking(false);
      setReason("");
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (approve) => {
    setBusy(true);
    try {
      await client.post(`/booking/${booking.booking_id}/cancel-decision`, { approve });
      toast.success(approve
        ? t("memberBookings.cancelApproved", { defaultValue: "Session cancelled." })
        : t("memberBookings.cancelDeclined", { defaultValue: "Cancellation declined — the session stands." }));
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="py-3 space-y-2" data-testid={`booking-row-${booking.booking_id}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="h-9 w-9">
            <AvatarImage src={booking.other_user?.picture} />
            <AvatarFallback className="bg-primary/10 text-primary text-xs">{initials(booking.other_user?.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{booking.other_user?.name}</p>
            <p className="text-xs text-muted-foreground font-mono flex items-center gap-1">
              <CalendarClock className="h-3 w-3" />
              {format(parseISO(booking.start_time), "MMM d, yyyy · HH:mm")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={booking.status} />
          {cancelPending && (
            <Badge variant="outline" className="rounded-full border-amber-500/40 bg-amber-500/10 text-amber-700 text-[10px]" data-testid={`booking-cancel-pending-${booking.booking_id}`}>
              {t("memberBookings.cancelPending", { defaultValue: "Cancellation requested" })}
            </Badge>
          )}
          {isMentorView && booking.status === "pending" && !cancelPending && (
            <>
              <Button size="sm" variant="outline" className="rounded-lg h-8" disabled={busy} onClick={() => updateStatus("confirmed")} data-testid="booking-confirm-button">
                {t("common.confirm")}
              </Button>
              <Button size="sm" variant="ghost" className="rounded-lg h-8 text-destructive" disabled={busy} onClick={() => updateStatus("cancelled")} data-testid="booking-cancel-button">
                {t("common.reject")}
              </Button>
            </>
          )}
          {!cancelPending && !reschedPending && booking.status !== "cancelled" && !isMentorView && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-lg h-8 gap-1.5"
              disabled={busy}
              onClick={openMove}
              data-testid={`booking-request-move-${booking.booking_id}`}
            >
              <CalendarSync className="h-3.5 w-3.5" />
              {t("memberBookings.proposeNewTime", { defaultValue: "Propose new time" })}
            </Button>
          )}
          {reschedPending && (
            <Badge variant="outline" className="rounded-full border-sky-500/40 bg-sky-500/10 text-sky-700 text-[10px]" data-testid={`booking-move-pending-${booking.booking_id}`}>
              {t("memberBookings.movePending", { defaultValue: "New time proposed" })} · {slotLabel(resched.start_time)}
            </Badge>
          )}
          {reschedPending && canDecide && (
            <>
              <Button size="sm" className="btn-brand rounded-lg h-8 gap-1.5" disabled={busy} onClick={() => decideMove(true)} data-testid={`booking-move-approve-${booking.booking_id}`}>
                <Check className="h-3.5 w-3.5" />
                {t("memberBookings.acceptNewTime", { defaultValue: "Accept new time" })}
              </Button>
              <Button size="sm" variant="ghost" className="rounded-lg h-8 gap-1.5" disabled={busy} onClick={() => decideMove(false)} data-testid={`booking-move-decline-${booking.booking_id}`}>
                <X className="h-3.5 w-3.5" />
                {t("memberBookings.keepOriginal", { defaultValue: "Keep original" })}
              </Button>
            </>
          )}
          {!cancelPending && booking.status !== "cancelled" && !isMentorView && (
            <Button
              size="sm"
              variant="ghost"
              className="rounded-lg h-8 text-destructive gap-1.5"
              disabled={busy}
              onClick={() => setAsking((v) => !v)}
              data-testid={`booking-request-cancel-${booking.booking_id}`}
            >
              <Ban className="h-3.5 w-3.5" />
              {t("memberBookings.requestCancel", { defaultValue: "Request cancellation" })}
            </Button>
          )}
          {cancelPending && canDecide && (
            <>
              <Button size="sm" variant="outline" className="rounded-lg h-8" disabled={busy} onClick={() => decide(true)} data-testid={`booking-cancel-approve-${booking.booking_id}`}>
                {t("memberBookings.approveCancel", { defaultValue: "Approve cancellation" })}
              </Button>
              <Button size="sm" variant="ghost" className="rounded-lg h-8" disabled={busy} onClick={() => decide(false)} data-testid={`booking-cancel-decline-${booking.booking_id}`}>
                {t("memberBookings.declineCancel", { defaultValue: "Keep session" })}
              </Button>
            </>
          )}
        </div>
      </div>

      {asking && (
        <div className="flex gap-2 flex-wrap rounded-xl border border-border bg-muted/20 p-2.5">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("memberBookings.reasonPlaceholder", { defaultValue: "Reason (optional) — helps the mentor reschedule" })}
            className="rounded-lg flex-1 min-w-[200px] h-9"
            data-testid={`booking-cancel-reason-${booking.booking_id}`}
          />
          <Button size="sm" className="btn-brand rounded-lg h-9" disabled={busy} onClick={requestCancel} data-testid={`booking-cancel-submit-${booking.booking_id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("memberBookings.submitRequest", { defaultValue: "Send request" })}
          </Button>
        </div>
      )}

      {moving && (
        <div className="rounded-xl border border-border bg-muted/20 p-2.5 space-y-2" data-testid={`booking-move-panel-${booking.booking_id}`}>
          {slots === null ? (
            <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
          ) : slots.length > 0 ? (
            <Select value={slot} onValueChange={setSlot}>
              <SelectTrigger className="rounded-lg h-9" data-testid={`booking-move-slot-${booking.booking_id}`}>
                <SelectValue placeholder={t("memberBookings.pickSlot", { defaultValue: "Pick an open slot" })} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {slots.slice(0, 60).map((s2) => (
                  <SelectItem key={s2.start_time} value={s2.start_time}>{slotLabel(s2.start_time)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                type="date"
                value={manual.date}
                onChange={(e) => setManual({ ...manual, date: e.target.value })}
                className="rounded-lg h-9"
                data-testid={`booking-move-date-${booking.booking_id}`}
              />
              <Input
                type="time"
                value={manual.time}
                onChange={(e) => setManual({ ...manual, time: e.target.value })}
                className="rounded-lg h-9"
                data-testid={`booking-move-time-${booking.booking_id}`}
              />
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("memberBookings.moveReasonPlaceholder", { defaultValue: "Why the change? (optional)" })}
              className="rounded-lg flex-1 min-w-[180px] h-9"
              data-testid={`booking-move-reason-${booking.booking_id}`}
            />
            <Button size="sm" className="btn-brand rounded-lg h-9" disabled={busy} onClick={proposeTime} data-testid={`booking-move-submit-${booking.booking_id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("memberBookings.sendProposal", { defaultValue: "Send proposal" })}
            </Button>
          </div>
        </div>
      )}

      {reschedPending && (
        <p className="text-[11px] text-muted-foreground ps-12" data-testid={`booking-move-note-${booking.booking_id}`}>
          {t("memberBookings.moveFromTo", {
            defaultValue: "Moving from {{from}} to {{to}}",
            from: slotLabel(resched.previous_start_time || booking.start_time),
            to: slotLabel(resched.start_time),
          })}
          {resched.reason ? ` — “${resched.reason}”` : ""}
        </p>
      )}

      {cancelPending && (
        <p className="text-[11px] text-muted-foreground ps-12" data-testid={`booking-cancel-reason-text-${booking.booking_id}`}>
          {t("memberBookings.requestedBy", { defaultValue: "Requested by" })} {req.requested_by_name || t("common.member", { defaultValue: "Member" })}
          {req.reason ? ` — “${req.reason}”` : ""}
        </p>
      )}
      {req.status === "declined" && (
        <p className="text-[11px] text-muted-foreground ps-12">
          {t("memberBookings.declinedNote", { defaultValue: "A cancellation request was declined." })}
        </p>
      )}
    </div>
  );
}

export function DashboardBookingsTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = useCallback(() => {
    client.get("/booking/my").then((res) => setData(res.data)).catch(() => setData({ as_requester: [], as_mentor: [] }));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-bookings-tab">
      <Card className="rounded-2xl border border-border p-5">
        <h3 className="font-display font-semibold mb-2">{t("dashboard.asRequester")}</h3>
        {data.as_requester.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">{t("dashboard.noBookings")}</p>
        ) : (
          <div className="divide-y divide-border" data-testid="my-bookings-table">
            {data.as_requester.map((b) => (
              <BookingRow key={b.booking_id} booking={b} isMentorView={false} isAdmin={isAdmin} onUpdated={load} />
            ))}
          </div>
        )}
      </Card>

      {(user?.role === "mentor" || data.as_mentor.length > 0) && (
        <Card className="rounded-2xl border border-border p-5">
          <h3 className="font-display font-semibold mb-2">{t("dashboard.asMentor")}</h3>
          {data.as_mentor.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">{t("dashboard.noBookings")}</p>
          ) : (
            <div className="divide-y divide-border" data-testid="mentor-bookings-table">
              {data.as_mentor.map((b) => (
                <BookingRow key={b.booking_id} booking={b} isMentorView={true} isAdmin={isAdmin} onUpdated={load} />
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
